/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useRef, useState } from "react";

import cn from "classnames";

import { useDetectClickOutside } from "utils/useDetectClickOutside";

import { ArrowDown } from "components/icons/common";

import { Input } from "../Input";
import { LoadingDots } from "../Loaders";
import Stack from "../Stack";
import Text from "../Text";

import countries_codes from "./codes";
import flagPosition from "./telephone.module.css";
import { getEnvs } from "state/EnvironmentsContext/EnvContext";
import { getDefaultCountryCode } from "lib/v3/primaryEventsApi";
import { getEventId } from "Router";

const PhoneInput = ({ onChange, isMissing, countryCode, ...props }) => {
  const countryCodePrefix = countryCode ? `+${countryCode}` : "";

  return (
    <div className="relative w-full">
      {countryCodePrefix && (
        <span
          className={cn(
            "absolute start-4 top-1/2 -translate-y-1/2 pointer-events-none select-none",
            "text-base leading-6 text-[#6C6C6C]",
          )}
        >
          {countryCodePrefix}
        </span>
      )}

      <Input
        type="text"
        placeholder="Phone Number"
        maxLength={12}
        onChange={(e) => {
          const { value } = e.target;
          let val = value;
          if (value.startsWith("0")) {
            val = value.slice(1);
          }

          val = val.replace(/[^0-9]/g, "");
          onChange(val);
        }}
        {...props}
        className={cn("!mt-0", countryCodePrefix && "ps-14", props.className)}
        error={isMissing}
        test-id="ZcFiLd4E"
      />
    </div>
  );
};

export const CountryCodeInput = ({
  value,
  onChange,
  disabled,
  isMissing,
  isShowSelectedOptionLabel,
  testId,
}) => {
  const boxRef = useRef(null);
  useDetectClickOutside(boxRef, () => {
    if (!isOpen && filteredOptions.length === 0) return;
    setFilteredOptions([]);
    setOpen(false);
  });

  const [isOpen, setOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);

  const [selectedOption, setSelectedOption] = useState({
    label: "United States (+1)",
    value: "1",
    id: "US",
  });

  useEffect(() => {
    if (value === selectedOption.value) return;

    if (value && typeof value === "string") {
      const _value = value.replace("+", "");

      const target = countries_codes.find(
        (country) =>
          country.value === _value ||
          country.label === _value ||
          country.id === _value,
      );

      if (target) {
        setSelectedOption(target);
      }
    }
  }, [value]);

  const onSelect = (option) => {
    if (option.id === selectedOption?.id) return;
    setSelectedOption(option);
    setOpen(false);

    if (onChange) {
      onChange(option.value);
    }
  };

  const [isSearching, setSearching] = useState(false);
  const timeoutRef = useRef(null);
  const prevSearch = useRef("");

  const onSearch = (search) => {
    if (prevSearch.current === search) return;
    setSearching(true);

    timeoutRef.current = setTimeout(async () => {
      prevSearch.current = search;
      const _filteredOptions = countries_codes.filter((option) => {
        const _label = option.label.toLowerCase();
        const _value = option.value.toLowerCase();
        const _id = option.id.toLowerCase();
        const _search = search.toLowerCase();
        return (
          _label.includes(_search) ||
          _value.includes(_search) ||
          _id.includes(_search)
        );
      });

      setFilteredOptions(_filteredOptions);
      setSearching(false);
    }, 200);
  };

  return (
    <div
      ref={boxRef}
      className={cn("w-[76px] shrink-0", {
        "w-full": isShowSelectedOptionLabel,
      })}
      test-id={testId}
    >
      <div
        className={cn(
          "relative flex items-center gap-1 w-[76px] mr-1 border bg-transparent text-white h-[50px] rounded-none px-[6px] py-2.5 border-light-gray",
          { "border-alert": isMissing, "w-full": isShowSelectedOptionLabel },
        )}
        onClick={() => {
          if (disabled) return;
          setOpen(!isOpen);
        }}
        test-id="BPJFco3p"
      >
        <div
          className="ms-1 text-sm leading-5 opacity-80 select-none whitespace-nowrap"
          test-id="loR8f2TG"
        >
          {selectedOption?.value ? `+${selectedOption.value}` : ""}
        </div>

        {isShowSelectedOptionLabel && (
          <Text className="text-white ml-2" test-id="Eoordx91">
            {selectedOption.label}
          </Text>
        )}

        <div className="ms-auto shrink-0" test-id="iLYZEuaq">
          <ArrowDown test-id="7-UsmTQG" />
        </div>
      </div>

      <Stack className="relative" test-id="blB9INdZ">
        {isOpen && (
          <ul
            className={cn(
              "w-80 border-light-gray bg-primary transition-all duration-200 absolute z-[30] list-none max-h-64 overflow-auto border-2 py-1",
            )}
            test-id="0QeAYuZo"
          >
            <Stack
              as="li"
              className="sticky -top-1 -mt-1 bg-black"
              test-id="4564wf3X"
            >
              <Input
                placeholder="Search..."
                onKeyUp={(e) => {
                  clearTimeout(timeoutRef.current);

                  if (e.target.value.length === 0) {
                    setFilteredOptions([]);
                    setSearching(false);
                    return;
                  }

                  onSearch(e.target.value);
                }}
                test-id="_EE-M1Az"
              />

              <div
                className={cn(
                  "absolute right-8 top-4",
                  !isSearching && "hidden",
                )}
                test-id="AUaxGVUl"
              >
                <LoadingDots test-id="qzQPDyjc" />
              </div>
            </Stack>

            {filteredOptions.length > 0
              ? filteredOptions.map(({ id, value, label }) => {
                  return (
                    <li
                      key={id}
                      className={cn(
                        "py-1 px-3 flex space-x-s15 justify-between bg-transparent hover:bg-[#242424]",
                      )}
                      role="button"
                      onClick={() => onSelect({ id, value, label })}
                      test-id="kDx86MxU"
                    >
                      <div
                        className="flex space-x-s15 items-center"
                        test-id="J2ztJgrX"
                      >
                        <div
                          className={cn(
                            flagPosition.flag,
                            flagPosition[id.toLowerCase()],
                          )}
                          test-id="dv-VxEH-"
                        />

                        <Text test-id="RJy9noXM" className="text-white">
                          {label}
                        </Text>
                      </div>

                      <Text className="ml-auto text-white" test-id="0-8bSm6n">
                        {selectedOption &&
                          selectedOption.value === value &&
                          "✓"}
                      </Text>
                    </li>
                  );
                })
              : countries_codes.map(({ id, value, label }) => {
                  return (
                    <li
                      key={id}
                      className={cn(
                        "py-1 px-3 flex space-x-s15 justify-between bg-transparent hover:bg-[#242424]",
                      )}
                      role="button"
                      onClick={() => onSelect({ id, value, label })}
                      test-id="4yk0qfBT"
                    >
                      <div
                        className="flex space-x-s15 items-center"
                        test-id="vSdNwXGU"
                      >
                        <div
                          className={cn(
                            flagPosition.flag,
                            flagPosition[id.toLowerCase()],
                          )}
                          test-id="pCueAgL9"
                        />

                        <Text className="text-white" test-id="Eoordx91">
                          {label}
                        </Text>
                      </div>

                      <Text className="ml-auto text-white" test-id="5xKF3aSk">
                        {selectedOption &&
                          selectedOption.value === value &&
                          "✓"}
                      </Text>
                    </li>
                  );
                })}
          </ul>
        )}
      </Stack>
    </div>
  );
};

const MyPhoneInput = ({
  countryCode = "",
  setCountryCode = (_value) => {},
  value = "",
  onChange = (_value) => {},
  disabled = false,
  isMissing = false,
  isCodeMissing = false,
  testId = "phone-input-default",
}) => {
  const eventId = getEventId();
  const firstRender = useRef(true);
  const code = useRef(countryCode);
  const isMounted = useRef(true);

  // the default-code request can resolve after unmount (e.g. switching tabs),
  // where `code` is stale and would overwrite the parent's real country code.
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    code.current = countryCode;

    if (countryCode || !firstRender.current) return;

    const getEventDefaultCountryCode = async () => {
      const res = eventId
        ? await getDefaultCountryCode(eventId)
        : { ok: false };

      if (!isMounted.current || code.current) return;

      if (res.ok) {
        setCountryCode(res.data.country_code);
      } else {
        setCountryCode(getEnvs((state) => state.country_code));
      }
    };

    if (firstRender.current) {
      firstRender.current = false;
    }

    getEventDefaultCountryCode();
  }, [countryCode]);

  return (
    <div className="flex items-center space-x-3" test-id={testId}>
      <CountryCodeInput
        value={countryCode}
        onChange={(code) => setCountryCode(code)}
        disabled={disabled}
        isMissing={isCodeMissing}
        testId={`${testId}-country-code`}
      />

      <Stack>
        <PhoneInput
          value={value}
          onChange={(phoneNumber) => onChange(phoneNumber)}
          countryCode={countryCode}
          disabled={disabled}
          isMissing={isMissing}
          testId={`${testId}-phone-number`}
        />
      </Stack>
    </div>
  );
};

export default MyPhoneInput;
